import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { ForgotPasswordButton } from "../../ForgotPasswordButton";
import { useLanguage } from "../../Language";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import axios from "axios";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";
import { AccountHolder, useAccount } from "../../Account";
import { useForm, SubmitHandler } from "react-hook-form";

interface EditUsernameFormInput {
  username: string;
  password: string;
}

export interface UpdateLocalUserReturn {
  success: boolean;
  message: string;
  alreadyInUse: boolean;
}

export interface Credentials {}

export interface EZLoginInformation {
  username: string;
  password?: string;
  credentials: Credentials;
}

export const EditUsernamePage: React.FC = () => {
  const navigate = useNavigate();
  const { translate } = useLanguage();
  const { user } = useAccount();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EditUsernameFormInput>({
    mode: "onBlur",
  });
  const [errorMessage, setErrorMessage] = React.useState<string>();

  const onSubmit: SubmitHandler<EditUsernameFormInput> = async ({
    password,
    username,
  }) => {
    const { data } = await axios.post<UpdateLocalUserReturn>(
      `account/update/username`,
      {
        username,
        credentials: {
          username: user.username,
          password,
          useLocalLoginMethod: true,
        },
      }
    );

    if (data.success === true) {
      SuccessMessageControl.show(translate("successUpdatingEZUsernameMessage"));
      await AccountHolder.fetchUser(true); // refetch the User object so the new username shows
      navigate("/myaccount/summary");
      return;
    }

    if (data.alreadyInUse === true) {
      setErrorMessage(translate("EZUsernameAlreadyInUseMessage"));
      return;
    }

    setErrorMessage(translate("errorUpdatingEZUsernameMessage"));
  };

  return (
    <>
      <Toolbar disableGutters={true}>
        <IconButton
          aria-label="back"
          onClick={() => navigate("/myaccount/summary")}
        >
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h5">{translate("updateEZUsername")}</Typography>
      </Toolbar>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack direction="column" sx={{
            gap: 2
          }}>
            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
            <Box>
              <span>{translate("currentEzUsername")}: </span>
              <strong>{user?.username}</strong>
            </Box>
            <TextField
              name="username"
              label={translate("newEzUsername")}
              type="text"
              required
              autoFocus
              {...register("username", {
                required: translate("requiredFieldError"),
              })}
              error={!!errors.username}
              helperText={errors.username?.message || " "}
            ></TextField>

            <TextField
              name="password"
              required
              {...register("password", {
                required: translate("requiredFieldError"),
              })}
              error={!!errors.password}
              helperText={
                errors.password?.message ||
                translate("enterOldPinForSecurityPurposes")
              }
              label={translate("ezPassword")}
              type="password"
            ></TextField>
            <Box>
              <ForgotPasswordButton />
            </Box>

            <Box>
              <Button type="submit" variant="contained">
                {translate("save")}
              </Button>
              <Button
                type="button"
                onClick={() => navigate("/myaccount/summary")}
              >
                {translate("cancel")}
              </Button>
            </Box>
          </Stack>
        </form>
      </Box>
    </>
  );
};
